import React, { useMemo } from "react"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { H4, H6, Small } from "@/components/standard/BFText"
import { Progress } from "@/components/reusables/ui/progress"
import { useQuery } from "@tanstack/react-query"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { Separator } from "@/components/reusables/ui/separator"
import { formatNumber } from "react-native-currency-input"
import { differenceInCalendarDays, format, isBefore } from "date-fns"
import { View } from "react-native"
import { type MilestoneType } from "@/generated/graphql"

interface CircleProps {
  className?: string
}

export const Circle: React.FC<CircleProps> = ({ className = "" }) => {
  return <View className={`size-2.5 rounded-full ${className}`} />
}

const isMilestoneActive = (milestone: MilestoneType) =>
  milestone.status !== "completed" && milestone.status !== "blocked"

interface LoanStatusCardProps {
  loanId: string
  className?: string
}

export const LoanStatusCard: React.FC<LoanStatusCardProps> = ({
  loanId,
  className = "",
}) => {
  const { data: loanApp } = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      return data!
    },
  })

  const milestones = loanApp?.milestones

  if (!milestones) {
    return null
  }

  const progressPercentage = milestones.reduce((acc, milestone) => {
    return acc + (milestone.percentageDone || 0)
  }, 0)

  const activeTasksOverdue = milestones.some((milestone) => {
    if (!isMilestoneActive(milestone) || !milestone.dueDate) {
      return false
    }

    const dueDate = new Date(milestone.dueDate)
    return isBefore(dueDate, new Date()) // Check if dueDate is in the past
  })

  const startedProcess = milestones[1]?.status === "completed"

  const headerText = useMemo(() => {
    if (isMilestoneActive(milestones[0])) {
      return "Preparing Loan Options"
    }
    if (milestones[1].status !== "completed") {
      return "Securing Terms"
    }

    return activeTasksOverdue ? "Needs Action" : "On Track"
  }, [milestones, activeTasksOverdue])

  const dotColor = useMemo(() => {
    if (!startedProcess) {
      return "bg-light-grey"
    }

    return activeTasksOverdue ? "bg-destructive" : "bg-success"
  }, [startedProcess, activeTasksOverdue])

  function DaysLeftText() {
    if (!startedProcess || !loanApp) return null

    if (activeTasksOverdue) {
      return (
        <Small className={"text-destructive"}>
          Delays may impact close date
        </Small>
      )
    }

    if (!(loanApp.processing?.currentCloseDate || loanApp.targetCloseDate)) {
      return null
    }

    const closingDate = new Date(
      loanApp.processing?.currentCloseDate || loanApp.targetCloseDate || 0
    )

    const daysLeft = differenceInCalendarDays(closingDate, new Date())

    if (daysLeft > 0) {
      return (
        <Small>
          {daysLeft} day{daysLeft !== 1 ? "s" : ""}
        </Small>
      )
    }
  }

  return (
    <Vertical
      className={`bg-card border border-border rounded-md p-5 gap-3 ${className}`}
    >
      <Horizontal className="items-start gap-3 justify-between">
        <Horizontal className="flex items-center gap-2">
          <H4 color="default">{headerText}</H4>
          <Circle className={dotColor} />
        </Horizontal>
        <DaysLeftText />
      </Horizontal>
      <Progress
        value={progressPercentage}
        className="h-3"
        indicatorClassName={
          activeTasksOverdue ? "bg-destructive" : "bg-success"
        }
      />
      <Separator />
      <Horizontal className="items-end gap-3">
        <Horizontal className="flex-1 p-1 justify-between">
          <Small>Amount</Small>
          <H6>
            {loanApp.processing?.loanAmount || loanApp.totalLoanAmount
              ? formatNumber(
                  loanApp.processing?.loanAmount ||
                    loanApp.totalLoanAmount ||
                    0,
                  {
                    prefix: "$",
                    delimiter: ",",
                    separator: ".",
                  }
                )
              : "N/A"}
          </H6>
        </Horizontal>
        <Separator orientation="vertical" />
        <Horizontal className="flex-1 p-1 justify-between">
          <Small>Target Close</Small>
          <H6>
            {loanApp.targetCloseDate
              ? format(
                  new Date(
                    loanApp.processing?.currentCloseDate ||
                      loanApp.targetCloseDate
                  ),
                  "MMM dd"
                )
              : "TBD"}
          </H6>
        </Horizontal>
      </Horizontal>
    </Vertical>
  )
}
